import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Progress,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";

const CouponDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [couponData, setCouponData] = useState(null);
  const [stats, setStats] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    discount: "",
    termsAndConditions: "",
    upTo: "",
    validUntil: "",
    usageLimit: "",
    image: "",
  });

  useEffect(() => {
    fetchCouponDetails();
    fetchCouponStats();
  }, [id]);

  useEffect(() => {
    if (couponData) {
      setFormData({
        name: couponData?.name,
        title: couponData.title,
        discount: couponData.discount,
        termsAndConditions: couponData?.termsAndConditions,
        upTo: couponData.upTo,
        validUntil: new Date(couponData?.validUntil).toISOString().slice(0, 16),
        usageLimit: couponData?.usageLimit,
        image: couponData?.image || "",
      });
    }
  }, [couponData]);

  const fetchCouponDetails = async () => {
    try {
      const response = await axiosInstance.get(`/api/v1/admin/coupons/${id}`);
      setCouponData(response.data);
    } catch (error) {
      console.error("Error fetching coupon details:", error);
    }
  };

  const fetchCouponStats = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/admin/coupons/stats/${id}`
      );
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching coupon stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await axiosInstance.put(`/api/v1/admin/coupons/${id}`, formData);
      setEditMode(false);
      fetchCouponDetails();
      fetchCouponStats();
    } catch (error) {
      console.error("Error updating coupon:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this coupon?")) {
      try {
        await axiosInstance.delete(`/api/v1/admin/coupons/${id}`);
        navigate("/ecommerce-coupons");
      } catch (error) {
        console.error("Error deleting coupon:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value || 0);
  };
  const uploadImageToOm = async (file) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      const response = await axiosInstance.post(
        "api/v1/upload-image",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.images;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const imageUrl = await uploadImageToOm(file);
        setFormData({ ...formData, image: imageUrl });
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Coupon Details"
          breadcrumbItems={[
            { title: "Ecommerce", link: "#" },
            { title: "Coupons", link: "/ecommerce-coupons" },
            { title: "Coupon Details", link: "#" },
          ]}
        />

        <Row>
          <Col lg={4}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">Coupon Information</h4>
                  <div>
                    {editMode ? (
                      <>
                        <Button
                          color="success"
                          size="sm"
                          onClick={handleUpdate}
                          disabled={submitting}
                          className="me-2"
                        >
                          {submitting ? <Spinner size="sm" /> : "Save Changes"}
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => setEditMode(false)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => setEditMode(true)}
                          className="me-2"
                        >
                          Edit
                        </Button>
                        <Button color="danger" size="sm" onClick={handleDelete}>
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {editMode ? (
                  <Form>
                    <FormGroup>
                      <Label for="name">Coupon Code</Label>
                      <Input
                        id="name"
                        value={formData?.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        disabled
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label for="title">Title</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        required
                      />
                    </FormGroup>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="discount">Discount (%)</Label>
                          <Input
                            id="discount"
                            type="number"
                            value={formData.discount}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                discount: e.target.value,
                              })
                            }
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="upTo">Max Discount</Label>
                          <Input
                            id="upTo"
                            type="number"
                            value={formData.upTo}
                            onChange={(e) =>
                              setFormData({ ...formData, upTo: e.target.value })
                            }
                            required
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup>
                      <Label for="validUntil">Valid Until</Label>
                      <Input
                        id="validUntil"
                        type="datetime-local"
                        value={formData?.validUntil}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            validUntil: e.target.value,
                          })
                        }
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label for="usageLimit">Usage Limit</Label>
                      <Input
                        id="usageLimit"
                        type="number"
                        value={formData?.usageLimit}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            usageLimit: e.target.value,
                          })
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label for="termsAndConditions">Terms & Conditions</Label>
                      <Input
                        id="termsAndConditions"
                        type="textarea"
                        rows="4"
                        value={formData?.termsAndConditions}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            termsAndConditions: e.target.value,
                          })
                        }
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label for="image">Upload Image</Label>
                      <Input
                        id="image"
                        type="file"
                        onChange={handleImageChange}
                        accept="image/*"
                      />
                      {formData?.image && (
                        <div className="mt-2">
                          <img
                            src={formData?.image}
                            alt="Coupon"
                            style={{ maxWidth: "100px" }}
                          />
                        </div>
                      )}
                    </FormGroup>
                  </Form>
                ) : (
                  <>
                    <div className="text-center mb-4">
                      {couponData?.image ? (
                        <img
                          src={couponData?.image.startsWith("https") ? couponData?.image : `${process.env.REACT_APP_BASEURL}${couponData?.image}`}
                          alt={couponData.title}
                          className="img-fluid rounded"
                          style={{ maxHeight: "150px" }}
                        />
                      ) : (
                        <div className="bg-light rounded p-4">
                          <i className="ri-coupon-2-line font-size-24"></i>
                        </div>
                      )}
                    </div>

                    <div className="table-responsive">
                      <Table className="table-nowrap mb-0">
                        <tbody>
                          <tr>
                            <th scope="row">Code:</th>
                            <td>{couponData?.name}</td>
                          </tr>
                          <tr>
                            <th scope="row">Title:</th>
                            <td>{couponData?.title}</td>
                          </tr>
                          <tr>
                            <th scope="row">Discount:</th>
                            <td>
                              {couponData?.discount}% up to{" "}
                              {formatCurrency(couponData?.upTo)}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">Valid Until:</th>
                            <td>{formatDate(couponData?.validUntil)}</td>
                          </tr>
                          <tr>
                            <th scope="row">Usage Limit:</th>
                            <td>{couponData?.usageLimit || "Unlimited"}</td>
                          </tr>
                          <tr>
                            <th scope="row">Status:</th>
                            <td>
                              <Badge
                                color={
                                  new Date(couponData?.validUntil) > new Date()
                                    ? "success"
                                    : "danger"
                                }
                              >
                                {new Date(couponData?.validUntil) > new Date()
                                  ? "Active"
                                  : "Expired"}
                              </Badge>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    <div className="mt-4">
                      <h6 className="font-size-14 mb-3">Terms & Conditions:</h6>
                      <p className="text-muted">
                        {couponData?.termsAndConditions}
                      </p>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>

          <Col lg={8}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Usage Statistics</h4>

                <Row className="text-center mb-4">
                  <Col md={4}>
                    <div className="mt-4 mt-sm-0">
                      <div className="font-size-24 mb-1">
                        {stats.totalUsageCount}
                      </div>
                      <p className="text-muted mb-0">Total Uses</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mt-4 mt-sm-0">
                      <div className="font-size-24 mb-1">
                        {formatCurrency(stats.totalDiscountGiven)}
                      </div>
                      <p className="text-muted mb-0">Total Discount Given</p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mt-4 mt-sm-0">
                      <div className="font-size-24 mb-1">
                        {formatCurrency(stats.averageDiscount)}
                      </div>
                      <p className="text-muted mb-0">Average Discount</p>
                    </div>
                  </Col>
                </Row>

                {couponData?.usageLimit && (
                  <div className="mb-4">
                    <h6 className="font-size-14 mb-3">Usage Progress</h6>
                    <Progress
                      value={
                        (stats.totalUsageCount / couponData?.usageLimit) * 100
                      }
                      color="primary"
                      className="progress-sm"
                    >
                      {stats.totalUsageCount} of {couponData?.usageLimit}
                    </Progress>
                  </div>
                )}

                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>User</th>
                        <th>Used On</th>
                        <th>Original Amount</th>
                        <th>Discount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.userUsage.map((usage, index) => (
                        <tr key={index}>
                          <td>
                            <div>
                              <h6 className="mb-1">{usage.user.fullName}</h6>
                              <p className="text-muted mb-0">
                                {usage.user.email}
                              </p>
                            </div>
                          </td>
                          <td>{formatDate(usage.usedAt)}</td>
                          <td>{formatCurrency(usage.originalAmount)}</td>
                          <td>{formatCurrency(usage.discountAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CouponDetails;
